import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { backendApi } from '../lib/backendApi';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Mail } from 'lucide-react';

const ForgotPasswordPage = () => {
  const { api, logout } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const response = await backendApi.auth.requestPasswordReset(api, { email });
      setMessage(response.data?.message || 'If the email exists, a password reset email has been sent.');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = async (e) => {
    e.preventDefault();
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">
        <Card className="border-border/60 bg-card/75 shadow-xl backdrop-blur">
          <CardHeader>
            <CardTitle className="font-heading text-2xl flex items-center gap-2">
              <Mail className="h-5 w-5 text-primary" />
              Forgot Password
            </CardTitle>
            <CardDescription>
              Enter your email and we will send you a reset link plus a 6-digit reset code.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              {error ? <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert> : null}
              {message ? <Alert className="border-border/60 bg-background/70"><AlertDescription>{message}</AlertDescription></Alert> : null}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</> : 'Send Reset Email'}
              </Button>
            </form>

            <Button
              variant="outline"
              className="mt-4 w-full"
              onClick={() => navigate(`/reset-password?email=${encodeURIComponent(email)}`)}
            >
              I Already Have My Reset Email
            </Button>

            <p className="text-sm text-muted-foreground mt-4 text-center">
              Back to <Link className="text-primary hover:underline" to="/login" onClick={handleGoToLogin}>Login</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
